import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Divider, Dialog, DialogTitle,
  DialogContent, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, DialogActions, Button
} from '@mui/material';
import { EA_BASE_URL } from '../../strings';
import { getToken } from '../Authentication/get-token';

const DataTable = ({ title, data }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
      {title}
    </Typography>
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.200' }}>
            {data.headers.map((header) => (
              <TableCell key={header} sx={{ fontWeight: 'bold' }}>{header}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.rows.map((row) => (
            <TableRow key={row.label}>
              <TableCell component="th" scope="row" sx={{ fontWeight: 'medium' }}>
                {row.label}
              </TableCell>
              {row.values.map((value, index) => (
                <TableCell key={index} align="right">{value}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

const DeviceDetailModal = ({ deviceData, onClose }) => {
  if (!deviceData) return null;
  return (
    <>
      <DialogTitle>
        <Typography variant="h6">{deviceData.title}</Typography>
        <Typography variant="body2" color="text.secondary">{deviceData.timestamp}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {deviceData.perPhase && <DataTable title="Per-phase" data={deviceData.perPhase} />}
        {deviceData.lineLine && <DataTable title="Line-line" data={deviceData.lineLine} />}
        {deviceData.energy && <DataTable title="Energy" data={deviceData.energy} />}
        {deviceData.powerQuality && <DataTable title="Power quality" data={deviceData.powerQuality} />}
        {deviceData.flags && <DataTable title="Flags" data={deviceData.flags} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </>
  );
};

const TreeNode = ({ name, data = [], onHeaderClick }) => (
  <Card variant="outlined" sx={{ minWidth: 160, bgcolor: '#f7f7f7', border: '1px solid #e0e0e0' }}>
    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
      <Typography
        variant="body1"
        sx={{ fontWeight: 'bold', mb: 1, textAlign: 'center', cursor: 'pointer' }}
        onClick={() => onHeaderClick(name)}
      >
        {name}
      </Typography>
      <Divider />
      <Box sx={{ pt: 1 }}>
        {data.map((item, index) => (
          <Box key={index} sx={{ display: 'flex', justifyContent: 'center', px: 1, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">{item.unit}</Typography>
          </Box>
        ))}
      </Box>
    </CardContent>
  </Card>
);

const Breaker = () => (
  <Box sx={{
    width: 40, height: 40, bgcolor: 'red',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  }}>
    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'white' }}>cls</Typography>
  </Box>
);

const VerticalLine = () => (
  <Box sx={{ width: '2px', height: '20px', bgcolor: 'grey.400' }} />
);

function formatDate(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

// Utility to fetch measurement data from API
const fetchMeasureData = async (selectedFilters) => {
  try {
    const now = new Date();
    const twoDaysAgo = new Date(now.getTime() - 48 * 60 * 60 * 1000);
    const queryParams = new URLSearchParams({
      start_time: formatDate(twoDaysAgo),
      end_time: formatDate(now)
    });

    // Use measurements from selectedFilters instead of imported measureList
    const measurementArray = selectedFilters?.measurements || [];

    const requestBody = {
      siteId: selectedFilters.Site,
      oneLineDiagram: selectedFilters.OnelineDiagram,
      assetNames: selectedFilters.list,
      measureNameList: measurementArray
    };

    const getAuthHeaders = () => {
      const tokenObj = getToken();
      return {
        "Content-Type": "application/json",
        ...(tokenObj?.headers || {}),
      };
    };

    const response = await fetch(
      `${EA_BASE_URL}eadashboard/measure-list?${queryParams}`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody)
      }
    );

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    return await response.json();
  } catch (err) {
    console.error("Error fetching data:", err);
    return [];
  }
};


const formatTreeNodeData = (measureList = []) => {
  const desiredMeasures = new Set([
    "VAB",
    "IA",
    "KW_A"
  ]);

  // Return an empty array if the input is invalid or empty.
  if (!measureList) return [];

  const filteredList = measureList.filter(m => desiredMeasures.has(m.measureName));
  const getOrder = (measureName) => {
    if (measureName.startsWith('V')) return 1; // Voltage first
    if (measureName.startsWith('I')) return 2; // Current second
    if (measureName.startsWith('KW')) return 3; // Power third
    return 4; // Any other types last
  };

  filteredList.sort((a, b) => {
    const orderA = getOrder(a.measureName);
    const orderB = getOrder(b.measureName);
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return a.measureName.localeCompare(b.measureName);
  });

  return filteredList.map(measure => {
    let unitSymbol = '';
    let label = measure.measureName;

    if (label.startsWith('V')) unitSymbol = 'V';
    if (label.startsWith('I')) unitSymbol = 'A';
    if (label.startsWith('KW_')) {
      unitSymbol = 'kW';
      label = label.replace('_', ' ');
    }

    return {
      // value: label,
      unit: `${measure.measureValue} ${unitSymbol}`
    };
  });
};

function EvciEdsTreeData({ selectedFilters, refreshTrigger }) {
  const [fetchedData, setFetchedData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDeviceData, setSelectedDeviceData] = useState(null);

  useEffect(() => {
    async function loadData() {
      console.log(`Loading data for ${selectedFilters.RefreshRate} minute refresh interval...`);
      const data = await fetchMeasureData(selectedFilters);
      setFetchedData(data);
    }
    loadData();
  }, [selectedFilters, refreshTrigger]); // Added refreshTrigger to dependencies

  const handleHeaderClick = (deviceName) => {
    // Find the device data from fetchedData
    const deviceData = fetchedData.find(d => d.assetName === deviceName);

    // Helper function to get measure value by name
    const getMeasureValue = (measureName) => {
      const measure = deviceData?.measureNameList?.find(m => m.measureName === measureName);
      return measure ? measure.measureValue : 0;
    };

    // Helper function to convert boolean to number for flags
    const boolToNumber = (value) => {
      if (typeof value === 'boolean') {
        return value ? 1 : 0;
      }
      return value ? 1 : 0;
    };

    // Build the device details with actual measure values
    const deviceDetails = {
      title: `ABB Device Data - ${deviceName}`,
      timestamp: new Date().toLocaleString(),
      perPhase: {
        headers: ["Line", "A", "B", "C", "N", "3ph"],
        rows: [
          {
            label: "A",
            values: [
              getMeasureValue("IA"),
              getMeasureValue("IB"),
              getMeasureValue("IC"),
              getMeasureValue("IN"),
              "-"
            ]
          },
          {
            label: "AmpsTHD",
            values: [
              getMeasureValue("IA_THD"),
              getMeasureValue("IB_THD"),
              getMeasureValue("IC_THD"),
              getMeasureValue("IN_THD"),
              "-"
            ]
          },
          {
            label: "kW",
            values: [
              getMeasureValue("KW_A"),
              getMeasureValue("KW_B"),
              getMeasureValue("KW_C"),
              "-",
              getMeasureValue("KW_3PH")
            ]
          },
          {
            label: "kVAr",
            values: [
              getMeasureValue("KVAR_A"),
              getMeasureValue("KVAR_B"),
              getMeasureValue("KVAR_C"),
              "-",
              getMeasureValue("KVAR_3PH")
            ]
          },
          {
            label: "kVA",
            values: [
              getMeasureValue("KVA_A"),
              getMeasureValue("KVA_B"),
              getMeasureValue("KVA_C"),
              "-",
              getMeasureValue("KVA_3PH")
            ]
          },
          {
            label: "Volts L-N",
            values: [
              getMeasureValue("VAN"),
              getMeasureValue("VBN"),
              getMeasureValue("VCN"),
              "-",
              "-"
            ]
          }
        ]
      },
      lineLine: {
        headers: ["Line-Line", "A-B", "B-C", "C-A"],
        rows: [
          {
            label: "Volts-L-L",
            values: [
              getMeasureValue("VAB"),
              getMeasureValue("VBC"),
              getMeasureValue("VCA")
            ]
          },
          {
            label: "Volts THD",
            values: [
              getMeasureValue("VAB_THD"),
              getMeasureValue("VBC_THD"),
              getMeasureValue("VCA_THD")
            ]
          }
        ]
      },
      energy: {
        headers: ["Energy", "Pos", "Neg", "3ph"],
        rows: [
          {
            label: "kWH",
            values: [
              getMeasureValue("KWH_POS"),
              getMeasureValue("KWH_NEG"),
              getMeasureValue("KWH_3PH")
            ]
          },
          {
            label: "kVARH",
            values: [
              getMeasureValue("KVARH_POS"),
              getMeasureValue("KVARH_NEG"),
              getMeasureValue("KVARH_3PH")
            ]
          },
          {
            label: "kVAH",
            values: [
              getMeasureValue("KVAH_POS"),
              getMeasureValue("KVAH_NEG"),
              getMeasureValue("KVAH_3PH")
            ]
          }
        ]
      },
      powerQuality: {
        headers: ["Power quality", "Value"],
        rows: [
          {
            label: "Quality",
            values: [0]
          },
          {
            label: "Freq",
            values: [getMeasureValue("FREQ")]
          },
          {
            label: "PF",
            values: [getMeasureValue("PF_3PH")]
          },
          {
            label: "Residual V",
            values: [0]
          }
        ]
      },
      flags: {
        headers: ["Breaker Flags", "Value"],
        rows: [
          {
            label: "Closed",
            values: [boolToNumber(getMeasureValue("CB_CLOSED"))]
          },
          {
            label: "Open",
            values: [boolToNumber(getMeasureValue("CB_OPEN"))]
          },
          {
            label: "Tripped",
            values: [boolToNumber(getMeasureValue("CB_TRIPPED"))]
          },
          {
            label: "Connected",
            values: [boolToNumber(getMeasureValue("CB_CONNECTED"))]
          },
          {
            label: "Test Pos",
            values: [boolToNumber(getMeasureValue("CB_IN_TEST"))]
          },
          {
            label: "FdBk Conflict",
            values: [boolToNumber(getMeasureValue("CB_POS_UNDEFINED"))]
          },
          {
            label: "Comm Error",
            values: [0]
          }
        ]
      }
    };

    setSelectedDeviceData(deviceDetails);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedDeviceData(null);
  };

  const mainDevice = fetchedData.find(d => d.assetName.startsWith("Main"));
  const childDevices = fetchedData.filter(d => !d.assetName.startsWith("Main"));

  const childCount = childDevices.length;
  const horizontalBarOffset = childCount > 1 ? `calc(50% / ${childCount + 1})` : '0%';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4, overflowX: 'auto', background: '#fff' }}>
      <TreeNode
        name="Main"
        data={formatTreeNodeData(mainDevice?.measureNameList)}
        onHeaderClick={handleHeaderClick}
      />
      <VerticalLine />
      <Breaker />
      <VerticalLine />

      {childDevices.length > 0 && (
        <Box sx={{ position: 'relative', alignSelf: 'stretch', minWidth: `${childDevices.length * 200}px` }}>
          <Box sx={{ position: 'absolute', top: 0, left: horizontalBarOffset, right: horizontalBarOffset, height: '2px', bgcolor: 'grey.400' }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: '2px', gap: 2 }}>
            {childDevices.map(child => (
              <Box key={child.assetName} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <VerticalLine />
                <Breaker />
                <VerticalLine />
                <TreeNode
                  name={child.assetName}
                  data={formatTreeNodeData(child.measureNameList)}
                  onHeaderClick={handleHeaderClick}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Modal for device detail */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DeviceDetailModal deviceData={selectedDeviceData} onClose={handleCloseModal} />
      </Dialog>
    </Box>
  );
}

export default EvciEdsTreeData;